/**
 * A cadeia de regras da validação de ingresso.
 *
 * Porte de `br.com.tmobi.valida.CValidaService.validate()` do TMobi original,
 * mantendo a MESMA ordem de checagem, as mesmas mensagens e as mesmas cores.
 * Diferenças propositais em relação ao original estão marcadas com "MUDANÇA:".
 *
 * Esta função é pura em relação a I/O: tudo que ela precisa do banco entra pelo
 * `RepositorioValidacao`. É o que permite testá-la sem aparelho, sem câmera e
 * sem servidor — o validador antigo não tinha como.
 */

import {
  candidatosDeLote,
  codbarValido,
  interpretarLeitura,
  type JanelaValidade,
} from './codbar';
import {
  DESFECHO,
  IngressoCancelado,
  IngressoOnline,
  Lote,
  MotivoValidacao,
  ResultadoValidacao,
  Setor,
  STATUS_QUE_ENTRAM,
  Validado,
} from './tipos';

export interface RepositorioValidacao {
  buscarCancelado(
    eventoId: number,
    codbar: string,
  ): Promise<IngressoCancelado | null>;
  buscarValidado(codbar: string): Promise<Validado | null>;
  /**
   * Procura o lote do ingresso testando os candidatos na ordem recebida — o
   * primeiro que casar vence, então o mais específico vem primeiro.
   */
  buscarLote(candidatos: string[]): Promise<Lote | null>;
  buscarSetor(remoteId: number): Promise<Setor | null>;
  buscarOnline(codbar: string): Promise<IngressoOnline | null>;
  registrarValidado(validado: Validado): Promise<void>;
}

export interface OpcoesValidacao {
  /**
   * false pula a checagem de "já validado" — o original usa isso na revalidação
   * autorizada pelo operador.
   */
  checarJaValidado?: boolean;
  /**
   * true quando o operador já conferiu na tela os dados do portador de um
   * ingresso nominal e mandou seguir.
   */
  dadosConfirmados?: boolean;
  /** Injetável para o teste fixar a data. */
  agora?: () => Date;
}

function desfecho(
  motivo: MotivoValidacao,
  codbar: string,
  extra: Partial<ResultadoValidacao> = {},
): ResultadoValidacao {
  const {mensagem, cor, tinta, palavra, icone} = DESFECHO[motivo];
  return {
    valido: motivo === 'VALIDADO',
    motivo,
    mensagem,
    cor,
    tinta,
    palavra,
    icone,
    codbar,
    // Recusa oferece o botão de voltar à câmera, mas não fica presa nele: se o
    // operador se afastar para resolver com a pessoa, a tela se resolve sozinha
    // em dez segundos em vez de deixar o aparelho travado num desfecho velho.
    exigeConfirmacao: true,
    timeoutMs: 10000,
    ...extra,
  };
}


/** Dia e hora curtos, para caber na segunda linha da mensagem. */
function dataCurta(data: Date): string {
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)} ${dois(data.getHours())}:${dois(data.getMinutes())}`;
}

/**
 * O período do passaporte na segunda linha, como o SETOR_NAO_HABILITADO faz
 * com o nome do setor: antes de discutir com a pessoa, o operador precisa
 * saber para quando aquele ingresso vale.
 */
function periodoDe(janela: JanelaValidade): string {
  return `${dataCurta(janela.inicio)} até ${dataCurta(janela.fim)}`;
}

export async function validarIngresso(
  entrada: string,
  eventoId: number,
  repo: RepositorioValidacao,
  opcoes: OpcoesValidacao = {},
): Promise<ResultadoValidacao> {
  const {checarJaValidado = true, dadosConfirmados = false, agora = () => new Date()} = opcoes;

  // 0. O leitor devolve o conteúdo cru do QR — pode vir URL, prefixo, quebra
  // de linha, ou o JSON que o TMobi grava no ingresso. Daqui sai o código e,
  // quando o QR traz, a janela de validade do passaporte.
  const {codbar, janela} = interpretarLeitura(entrada);

  // 1. Dígito verificador.
  // MUDANÇA: o original tinha um `return` mudo no meio do preenchimento com
  // zeros — código curto e inválido não produzia resultado nenhum e a tela
  // ficava parada. Aqui todo caminho devolve um desfecho.
  if (!codbarValido(codbar)) {
    return desfecho('CODIGO_INVALIDO', codbar);
  }

  // 2. Passaporte fora da janela de datas.
  //
  // Porte de `CValidaActivity.handleResult`, que no original fazia isto na
  // Activity da câmera, antes da cadeia. Aqui é regra como as outras: vale
  // também para o código digitado à mão, que no original escapava da
  // checagem, e tem teste em cima.
  //
  // Vem antes do banco de propósito: não precisa de I/O, e ingresso do dia
  // errado não deveria nem contar como tentativa de reentrada.
  //
  // MUDANÇA: nenhuma no critério — a comparação continua contra o relógio do
  // aparelho, como no original. Portaria com a data errada erra dos dois
  // lados, mas hora de servidor exigiria rede, que é o que este app não pode
  // pressupor.
  if (janela) {
    const instante = agora().getTime();
    const dentro =
      janela !== 'ilegivel' &&
      instante >= janela.inicio.getTime() &&
      instante <= janela.fim.getTime();

    if (!dentro) {
      return desfecho('PASSAPORTE_FORA_DA_DATA', codbar, {
        mensagem:
          janela === 'ilegivel'
            ? DESFECHO.PASSAPORTE_FORA_DA_DATA.mensagem
            : `${DESFECHO.PASSAPORTE_FORA_DA_DATA.mensagem}\n${periodoDe(janela)}`,
      });
    }
  }

  // 3. Cancelado.
  const cancelado = await repo.buscarCancelado(eventoId, codbar);
  if (cancelado) {
    return desfecho('CANCELADO', codbar, {cancelado});
  }

  // 4. Já validado neste aparelho.
  if (checarJaValidado) {
    const validadoAntes = await repo.buscarValidado(codbar);
    if (validadoAntes) {
      return desfecho('JA_VALIDADO', codbar, {validadoEm: validadoAntes});
    }
  }

  // 5. Lote: 5 primeiros dígitos, com o formato antigo de 4 como reserva.
  const lote = await repo.buscarLote(candidatosDeLote(codbar));
  if (!lote) {
    return desfecho('LOTE_INVALIDO', codbar);
  }

  // 6. Setor do lote: precisa existir e estar habilitado nesta portaria.
  const setor = await repo.buscarSetor(lote.setorId);
  if (!setor) {
    return desfecho('SETOR_INVALIDO', codbar, {lote});
  }
  if (!setor.habilitado) {
    return desfecho('SETOR_NAO_HABILITADO', codbar, {
      lote,
      setor,
      mensagem: `${DESFECHO.SETOR_NAO_HABILITADO.mensagem}\n${setor.nome}`,
    });
  }

  // 7. O lote é deste evento.
  if (Number(lote.eventoId) !== Number(eventoId)) {
    return desfecho('OUTRO_EVENTO', codbar, {lote, setor});
  }

  // 8. Ingresso nominal: status precisa permitir entrada, e o operador confere
  // nome/CPF/e-mail antes de liberar.
  const online = await repo.buscarOnline(codbar);

  if (online) {
    const status = (online.status ?? '').toUpperCase().trim();

    // Cancelado na origem nem sempre chega na lista de cancelados do evento —
    // sem esta checagem um ingresso devolvido entraria.
    if (status === 'C') {
      return desfecho('STATUS_CANCELADO', codbar, {online, lote, setor});
    }
    if (!STATUS_QUE_ENTRAM.includes(status)) {
      return desfecho('STATUS_PENDENTE', codbar, {
        online,
        lote,
        setor,
        mensagem: `${DESFECHO.STATUS_PENDENTE.mensagem} (${status || 'sem status'})`,
      });
    }
  }

  if (online && !dadosConfirmados) {
    return desfecho('AGUARDANDO_CONFIRMACAO_DADOS', codbar, {
      online,
      lote,
      setor,
      consumacao: lote.consumacao,
    });
  }

  // 9. Grava a validação. É ela que sobe no próximo sync.
  // MUDANÇA: na v1 não existe a consulta ao terminal-mestre da rede local
  // (CValidaLocalService) — quem resolve duplicata entre aparelhos é o sync
  // com o servidor.
  const validado: Validado = {
    eventoId,
    codbar,
    dataHora: agora().toISOString(),
    setorId: String(setor.remoteId),
    consumacao: lote.consumacao,
    enviado: false,
  };
  await repo.registrarValidado(validado);

  // Entrou: a tela verde some sozinha e a câmera volta, sem tocar em nada — em
  // portaria movimentada, cada toque é uma pessoa parada na fila. Três segundos
  // porque a tela carrega setor, lote e número, e dois não davam para ler.
  // Recusa é o contrário: oferece o botão, porque o operador precisa resolver
  // com a pessoa, e só desiste depois de dez segundos.
  return desfecho('VALIDADO', codbar, {
    exigeConfirmacao: false,
    timeoutMs: 3000,
    consumacao: lote.consumacao,
    online: online ?? undefined,
    lote,
    setor,
    validadoEm: validado,
  });
}
